import { useMemo } from "react";
import classNames from "classnames";
import { TransactionForReconciliation } from "@common/contracts/reconciliation.contract";
import { currencyFormatter } from "@common/helpers/currency";
import { fullDateLongFormat } from "@common/helpers/date";
import { format, parseISO } from "date-fns";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import styles from "./Reconciliation.module.scss";
import { ReconciliationDateUpdateReason } from "./helpers";

interface Props {
    transaction?: TransactionForReconciliation;
    isAdded?: boolean;
    isRemoved?: boolean;
    dateToUpdate?: string;
    dateUpdateReason?: ReconciliationDateUpdateReason;
    linkIcon?: React.ReactNode;
    dragIcon?: React.ReactNode;
}

const DateUpdateReasonDescriptions: Record<
    ReconciliationDateUpdateReason,
    string
> = {
    [ReconciliationDateUpdateReason.MATCHED_WITH_STATMENT]:
        "Date will be updated to match statement",
};

export const ReconciliationTransaction: React.FC<Props> = ({
    transaction,
    isAdded,
    isRemoved,
    dateToUpdate: dateToUpdateString,
    dateUpdateReason,
    linkIcon,
    dragIcon,
}) => {
    const transactionDate = transaction?.date;
    const date = useMemo(
        () => (transactionDate ? parseISO(transactionDate) : undefined),
        [transactionDate],
    );
    const dateToUpdate = useMemo(
        () => (dateToUpdateString ? parseISO(dateToUpdateString) : undefined),
        [dateToUpdateString],
    );

    const className = classNames(styles.cell, {
        [styles.addedTransaction]: isAdded,
        [styles.removedTransaction]: isRemoved,
    });

    return (
        <>
            <Tooltip
                content={
                    dateUpdateReason && dateToUpdate
                        ? DateUpdateReasonDescriptions[dateUpdateReason]
                        : undefined
                }
            >
                <div className={classNames(className, styles.dateCell)}>
                    {date && (
                        <div
                            className={classNames({
                                [styles.updatedDate]: dateToUpdate,
                            })}
                        >
                            {format(date, fullDateLongFormat)}
                        </div>
                    )}
                    {dateToUpdate && (
                        <div>{format(dateToUpdate, fullDateLongFormat)}</div>
                    )}
                </div>
            </Tooltip>

            <div className={className}>
                {transaction?.bankDescription}
                {dragIcon}
            </div>
            <div className={classNames(className, styles.transactionAmount)}>
                {transaction && currencyFormatter.format(transaction.amount)}
                {linkIcon}
            </div>
        </>
    );
};
